/*
  Cart: a checkout button that follows you down the page, and the protector offer.

  Both are progressive enhancement. The markup ships with `hidden` on the bar and
  a plain <button> on the offer, so with JavaScript off the page is exactly what
  it is today: one checkout button at the bottom that works.
*/

/* ------------------------------------------------------- sticky checkout bar

   Measured on the live cart at 390x844: with a SINGLE item the checkout button
   sits 953px down a 844px screen. It is below the fold before the shopper has
   added anything, and every further item pushes it another card down. Desktop
   already solved this - .hcart__aside is position: sticky there - so this is
   the phone's half of the same idea.

   It shows only while the real button is off screen, and it carries the total,
   because a bare button asks somebody to commit without telling them to what.

   THE ROOTMARGIN IS THE WHOLE TRICK. The bar is fixed over the bottom of the
   viewport, so a naive observer gets into a loop: the button scrolls under the
   bar, the bar is covering it, "not visible" keeps the bar up, and the button
   can never get out from under it. Pulling the observer's bottom edge up by the
   bar's own height means the button only counts as visible once it is clear of
   where the bar sits, so the bar is always gone before it could cover it.

   It submits form="cart" with name="checkout", the same submit the real button
   does, so there is one checkout action on the page and not a copy of one.
*/
class StickyCheckout extends HTMLElement {
  connectedCallback() {
    this.target = document.getElementById('checkout');
    if (!this.target || !('IntersectionObserver' in window)) return;

    this.removeAttribute('hidden');
    this.classList.add('hcart__sticky--ready');

    this.remeasure = this.remeasure.bind(this);
    this.remeasure();
    window.addEventListener('resize', this.remeasure);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.remeasure);
    /* the cart re-renders this element on every quantity change; leaving the
       class behind would strand the bee halfway up a page with no bar on it */
    document.documentElement.classList.remove('hcart-sticky-in');
  }

  remeasure() {
    if (this.observer) this.observer.disconnect();

    /* its own height, read rather than assumed: the bar is one line on a phone
       and the total's width changes what wraps */
    const height = Math.ceil(this.getBoundingClientRect().height) || 72;

    this.observer = new IntersectionObserver(
      (entries) => {
        const clear = entries[entries.length - 1].isIntersecting;
        this.classList.toggle('hcart__sticky--in', !clear);

        /* The floating promo bee is fixed at bottom right in the root stacking
           context; this bar is inside .hcart, which is isolation: isolate at
           z-index 1, so no z-index here can lift the bar above it - it is in a
           sealed context underneath. Rather than unpick that (raising .hcart
           would put the whole cart over the bee everywhere else), the bee steps
           aside for the call to action, which is what it should do anyway. */
        document.documentElement.style.setProperty('--hcart-sticky-h', `${height}px`);
        document.documentElement.classList.toggle('hcart-sticky-in', !clear);
      },
      /* exactly the bar's height, not a pixel more: this margin puts the
         observer's bottom edge on the bar's top edge, so the button counts as
         visible the instant it would appear above the bar. Padding it out by a
         "safe" few pixels is what lets the real button peek over the top of the
         bar for a moment, with its twin sitting right underneath it. */
      { rootMargin: `0px 0px -${height}px 0px` }
    );
    this.observer.observe(this.target);
  }
}

/* ------------------------------------------------------------ protector offer

   One row, one tap, one item. Nothing is pre-selected and nothing is added that
   was not asked for: an add-on the shopper did not choose is a charge they did
   not agree to, whatever it does to the average order value.

   Liquid decides whether this is on the page at all - in stock, not already in
   the cart, and the cart holds at least one 4" Pop - so by the time this runs
   the only question left is whether the add succeeded.
*/
class CartProtector extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-protector-add]');
    this.message = this.querySelector('[data-protector-msg]');
    if (!this.button) return;
    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  onClick() {
    if (this.button.getAttribute('aria-busy') === 'true') return;
    const id = Number(this.button.dataset.variantId);
    if (!id) return;

    this.button.setAttribute('aria-busy', 'true');
    this.say('');

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then((result) => {
        if (!result.ok) {
          /* it can sell out between the page rendering and the tap; say what
             the server said rather than a cheerful guess */
          throw new Error(result.body.description || result.body.message || 'Could not add that.');
        }

        /* The cart's own element knows which bits of the page a cart change has
           to re-render, and it re-renders them from the server. Reusing it is
           what keeps the line items, the totals and the free shipping bar from
           ever disagreeing with each other - and it re-renders this offer away,
           since Liquid will not draw it again once a protector is in the cart. */
        const cart = document.querySelector('cart-items');
        if (cart && typeof cart.onCartUpdate === 'function') {
          cart.onCartUpdate();
          return;
        }
        window.location.reload();
      })
      .catch((error) => {
        this.say(error.message);
        this.button.removeAttribute('aria-busy');
      });
  }

  say(text) {
    if (this.message) this.message.textContent = text;
  }
}

if (!customElements.get('sticky-checkout')) customElements.define('sticky-checkout', StickyCheckout);
if (!customElements.get('cart-protector')) customElements.define('cart-protector', CartProtector);
